import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { getOAuthToken } from 'Helpers/api';
import { authStore } from 'Stores/AuthStore';

type RetryableConfig = AxiosRequestConfig & { _retry?: boolean };

const AUTH_ERROR_STATUSES = [401, 403];

let pendingToken: Promise<string | null> | null = null;
let pendingRefresh: Promise<void> | null = null;

const resolveToken = (): Promise<string | null> => {
	if (!pendingToken) {
		pendingToken = (async () => {
			await authStore.oauthTokenPromise;
			return getOAuthToken();
		})().finally(() => {
			pendingToken = null;
		});
	}
	return pendingToken;
};

const refreshToken = (): Promise<void> => {
	if (!pendingRefresh) {
		pendingRefresh = authStore.fetchOAuthToken().finally(() => {
			pendingRefresh = null;
		});
	}
	return pendingRefresh;
};

export const lmsAxiosClient = axios.create({
	baseURL: process.env.REACT_APP_LMS_API_URL as string
});

lmsAxiosClient.interceptors.request.use(async (config: AxiosRequestConfig) => {
	const token = await resolveToken();
	return {
		...config,
		headers: {
			...config.headers,
			'Content-Type': 'application/json',
			Authorization: `Bearer ${token ?? ''}`
		}
	};
});

lmsAxiosClient.interceptors.response.use(
	(res) => res,
	async (error: AxiosError) => {
		const config = error.config as RetryableConfig | undefined;
		const status = error.response?.status;
		if (
			!config ||
			config._retry ||
			!status ||
			!AUTH_ERROR_STATUSES.includes(status)
		) {
			return Promise.reject(error);
		}
		config._retry = true;
		try {
			const latestToken = await resolveToken();
			if (config.headers?.Authorization === `Bearer ${latestToken ?? ''}`) {
				await refreshToken();
			}
			return lmsAxiosClient(config);
		} catch {
			return Promise.reject(error);
		}
	}
);
